import {
  setPaymentFees,
  setPaymentID,
  setSignatoryName,
} from '../payment/paymentSlice';
import {
  resetDocumentUploadProgressStatus,
  setApplyApplicationNumber,
  setDocumentUploadCompleted,
  setDocumentUploadError,
  setDocumentUploadStarted,
  setDocumentUploadWorking,
} from './applySlice';
import { getCustomBreakdown } from '../../utils';

export const uploadApplicationDocuments = (onCompletionCallback) => {
  return async (dispatch, getState, api) => {
    let wasSuccesfull = true;
    let requiresPayment = true;
    let hasDocumentUploadError = false;

    try {
      dispatch(setDocumentUploadStarted(true));

      // Each type of document may have zero or more associated files
      // but requires only one upload token per type
      // So we will store the configuration details for each document type in an array
      const uploadConfiguration = [];

      // Get the documents from Redux
      const { documents, certifications } = getState().apply?.applicationData;

      let newApplication;
      try {
        // Apply for a new license and get the license number
        newApplication = await api.createNewLicense(getState());
      } catch (err) {
        wasSuccesfull = false;
        dispatch(resetDocumentUploadProgressStatus());
        dispatch(
          setDocumentUploadError(
            'An error occurred while attempting to apply for a new license.'
          )
        );
      }

      if (!newApplication) {
        wasSuccesfull = false;
        dispatch(resetDocumentUploadProgressStatus());
        dispatch(
          setDocumentUploadError(
            'The new license could not be successfully created.'
          )
        );
        return;
      }

      const { applicationNumber } = newApplication;
      dispatch(setApplyApplicationNumber(applicationNumber));

      if (documents) {
        try {
          // Get a list of the document types (documents are stored in Redux with the document type as their key)
          const documentTypes = Object.keys(documents);
          // Upload the documents using the new license number
          for (const documentType in documentTypes) {
            const docTypeName = documentTypes[documentType];

            // Get an upload token for each document type
            const { uploadToken } = await api.fetchDocumentUploadToken(
              applicationNumber,
              docTypeName
            );
            // Store the token and associated files in the array as its own config object
            uploadConfiguration.push({
              document: documentType,
              uploadToken: uploadToken,
              files: documents[docTypeName],
            });
          }

          if (
            !uploadConfiguration ||
            uploadConfiguration.length === 0 ||
            uploadConfiguration.filter((config) => config.files.length === 0)
              .length === uploadConfiguration.length
          ) {
            // If there is no uploadConfiguration, it's empty or it only contains configs with no associated files, then we're done.
            dispatch(setDocumentUploadCompleted(true));
          } else {
            dispatch(setDocumentUploadWorking(true));
            // Do the upload
            for (const uploadConfig of uploadConfiguration) {
              const { files } = uploadConfig;
              const { attachedFiles } = files;
              for (const documentFile of attachedFiles) {
                const { blobFile } = documentFile;
                await api.uploadDocument({
                  uploadToken: uploadConfig.uploadToken,
                  file: blobFile,
                });
              }
            }
          }
        } catch (err) {
          wasSuccesfull = false;
          hasDocumentUploadError = true;
        }

        if (hasDocumentUploadError) {
          dispatch(
            setDocumentUploadError(
              'An unexpected error occurred while attempting to upload documents.'
            )
          );
        }
      }

      let paymentCalculation;
      try {
        // Calculate payment
        paymentCalculation = await api.calculateLicenseFee(applicationNumber);
      } catch (err) {
        wasSuccesfull = false;
        dispatch(resetDocumentUploadProgressStatus());
        dispatch(
          setDocumentUploadError(
            'An error occurred while validating licensing fees.'
          )
        );
      }

      const {
        paymentId,
        summary,
        ccFee,
        achFee,
        ccTotal,
        achTotal,
        checkFee,
        checkTotal,
        details,
      } = paymentCalculation;
      const customBreakdown = getCustomBreakdown(details);

      const { fee, custom_fee, fee_override, custom_fee_multiple_answers } =
        getState()?.config?.settings?.express?.registration?.application;
      const licenseVerbiage =
        getState()?.config?.settings?.express?.registration?.type_verbiage;

      requiresPayment =
        (fee && fee !== 0) ||
        custom_fee ||
        fee_override ||
        custom_fee_multiple_answers;

      if (summary.total === 0) {
        requiresPayment = false;
      }

      let breakdown;

      if (customBreakdown.length > 0) {
        breakdown = customBreakdown;
      } else {
        breakdown = [
          {
            key: 'licenseFee',
            label: `${licenseVerbiage} Fee`,
            value: summary.total,
          },
        ];
      }

      const paymentFee = {
        ccFee,
        ccTotal,
        achFee,
        achTotal,
        checkFee,
        checkTotal,
        breakdown,
        summary,
      };
      dispatch(setPaymentID(paymentId));
      dispatch(setPaymentFees(paymentFee));
      dispatch(setSignatoryName(certifications.signatory));
      dispatch(setDocumentUploadCompleted(true));
    } catch (err) {
      dispatch(resetDocumentUploadProgressStatus());
      dispatch(setDocumentUploadError('An unexpected error occurred.'));
    } finally {
      onCompletionCallback(wasSuccesfull, requiresPayment);
    }
  };
};

export const getExistingLicensesForAPN = (apn, unitNumber = '') => {
  return async (dispatch, getState, api) => {
    const data = await api.lookupAPNAddress(apn, unitNumber);
    const result = {
      inUse: false,
      details: undefined,
      hasError: false,
      errorMessage: '',
      isResidential: data?.parcelSpecificInfo?.residential,
    };
    if (data.Code === 'DkNotFound') {
      result.hasError = true;
      result.errorMessage = data.Message;
      return result;
    }
    if (data && data.licenses?.length > 0) {
      result.inUse = true;
      result.details = [
        ...data.licenses.map((l) => {
          return {
            type: l.type,
            registrantName: l.registrant.name,
            permitNumber: l.number,
            propertyAddress: data.address,
            expirationDate: l.registrant.expiryDate,
          };
        }),
      ];
    }
    return result;
  };
};
